<template>
  <div class="predictive-search">
    <div class="predictive-search__inner">
      <div
        v-if="!isMobile"
        class="predictive-search__header"
      >
        <button
          class="predictive-search__close icon-button"
          type="button"
          @click.prevent="handleClose"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.close')"
          />

          <icon-close />
        </button>
      </div>

      <div
        v-if="searchQuery && !noResults"
        class="predictive-search__body"
        js-overlay="scrollElement"
      >
        <predictive-search-results
          :articles="searchResults.articles"
          :collections="searchResults.collections"
          :pages="searchResults.pages"
          :products="searchResults.products"
          :suggestions="searchResults.queries"
          :search-query="searchQuery"
        />
      </div>

      <div
        v-else
        class="predictive-search__empty"
        js-overlay="scrollElement"
      >
        <predictive-search-empty
          :search-query="searchQuery"
        />
      </div>

      <transition name="fade">
        <div
          v-if="searchQuery && !noResults"
          class="predictive-search__footer"
        >
          <btn
            class="predictive-search__view-all"
            modifiers="primary block center"
            :label="
              $string(
                'predictive_search.view_all',
                { replace: { term: searchQuery } },
              )
            "
            :url="`${$variable('routes.search')}?q=${searchQuery}&type=product`"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Predictive search (predictive-search)
 * -----------------------------------------------------------------------------
 * Overlay to display predictive search results.
 *
 * @param {Object} overlay - Overlay API.
 * @param {String} searchQuery - Current search query.
 *
 */
import { mapState } from 'vuex'

import Btn from '~global/btn/btn'
import PredictiveSearchEmpty from '~async/predictive-search/predictive-search-empty'
import PredictiveSearchResults from '~async/predictive-search/predictive-search-results'

import IconClose from '~icons/directional-navigation/close.svg'

import predictiveSearchQuery from '~graphql/predictive-search.gql'

import { formatPredictiveSearchResults } from '~/format/format-predictive-search'

export default {
  name: 'PredictiveSearch',

  components: {
    Btn,
    IconClose,
    PredictiveSearchEmpty,
    PredictiveSearchResults,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },

  emits: {
    'search-input': (payload) => typeof payload === 'string',
    clear: () => true,
  },

  data() {
    return {
      state: 'loading',
      searchResults: {
        articles: [],
        collections: [],
        pages: [],
        products: [],
        queries: [],
      },
      searchVariables: {
        country: cnvs.store.country,
        language: cnvs.store.language,
        query: '',
        types: [
          'ARTICLE',
          'COLLECTION',
          'PAGE',
          'PRODUCT',
          'QUERY',
        ],
        searchableFields: [
          'TITLE',
          'PRODUCT_TYPE',
          'VARIANTS_TITLE',
          'VARIANTS_SKU',
          'VENDOR',
          'BODY',
        ],
      },
      controller: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns true if no reuslts are found.
     */
    noResults() {
      return !this.searchResults.articles.length &&
        !this.searchResults.collections.length &&
        !this.searchResults.pages.length &&
        !this.searchResults.products.length &&
        !this.searchResults.queries.length
    },
  },

  watch: {

    /**
     * Watch for changes in entered search query.
     */
    searchQuery() {
      this.searchVariables.query = this.searchQuery
      this.runSearchQuery()
    },
  },

  mounted() {
    if (!this.overlay.isActive) {
      return
    }

    if (!this.searchQuery) {
      return
    }

    this.searchVariables.query = this.searchQuery
    this.runSearchQuery()
  },

  methods: {

    /**
     * Run search query and sets the results
     */
    async runSearchQuery() {
      if (this.controller.abort) {
        this.controller.abort()
      }

      try {
        this.controller = new AbortController()
        const response = await cnvs.Query({
          query: predictiveSearchQuery,
          signal: this.controller.signal,
          variables: {
            ...this.searchVariables,
          },
        })

        this.searchResults = formatPredictiveSearchResults(response)

        await this.$nextTick()
        this.overlay.refresh({ currentElement: document.activeElement })

      } catch (error) {
        cnvs.ReportError('Failed to perform search', error)
      }
    },

    /**
     * Emits search input changes.
     * @param {String} value - search input.
     */
    searchInput(value) {
      this.$emit('search-input', value)
    },

    /**
     * Clear search query by emiting event and changing search state.
     */
    clear() {
      this.$emit('clear')
    },

    /**
     * Close predictive search overlay.
     */
    handleClose() {
      this.overlay.close()
    },
  },
}
</script>

<style lang="scss">
@import './predictive-search';
@import './predictive-search-empty';
@import './predictive-search-results';
</style>
